// Read promo_code from URL query (?promo_code=CODE) for pre-filling checkout.
//
// The app rewrites the address as it opens a product (a deep link such as
// /creator/dp_55?promo_code=X ends up at /creator?page=digital_products&...),
// and the rewrite drops promo_code before the checkout mounts and reads it. So
// the code is also remembered once, when the page first loads
// (rememberPromoCodeFromUrl, called from index.js before React mounts), and
// handed back here when the address no longer carries it.

// The one sessionStorage key, holding exactly {code, scope, at, target} and nothing else.
export const PROMO_CODE_STORAGE_KEY = 'wm_promo_code';

// A remembered code stops applying after this long. It has to outlive the app's
// own address rewrite (about 2 seconds after load) and a visitor's first
// checkout: landing, filling in the form and a 1:1 coaching application that
// reloads the page before payment. 2 hours is generous for that. What stops the
// code following the visitor to a different product is the target match below,
// not the age: a seasonal code is locked to one product, and several checkouts
// auto-apply whatever getPromoCodeFromUrl() returns.
export const PROMO_CODE_MAX_AGE_MS = 2 * 60 * 60 * 1000;

// The query params that say which product an address points at, across every
// kind of link the app opens (see shortProductLink.js): id (digital product,
// course, URL product, membership), product_id (custom product), tab_id
// (community), tier (membership, 1:1 coaching), collection_id (course, once
// the app has rewritten the address).
const PRODUCT_PARAMS = ['id', 'product_id', 'tab_id', 'tier', 'collection_id'];

const cleanCode = (value) => (
    (value && typeof value === 'string' && value.trim() !== '') ? value.trim() : ''
);

// Which hub the page belongs to: the host plus the first path segment. A creator's
// hub on the platform lives at host/<creator>; a custom domain mounts the hub at
// '/', where the first segment is empty. A code remembered for one hub is never
// handed to another.
const currentScope = () => {
    const { host, pathname } = window.location;
    const firstSegment = String(pathname || '').split('/')[1] || '';
    return `${String(host || '').toLowerCase()}/${firstSegment.toLowerCase()}`;
};

// Which product the address points at: the value of each PRODUCT_PARAMS param it
// carries, e.g. {id: '55'}. Empty for a link to the hub itself.
const productTarget = (params) => {
    const target = {};
    PRODUCT_PARAMS.forEach((key) => {
        const value = (params.get(key) || '').trim();
        if (value) target[key] = value;
    });
    return target;
};

// Call once when the page first loads, before React mounts. Does nothing unless
// the address carries a promo_code. Storage can throw (private mode, in-app
// browsers), and a promo code must never stop the page, so every access is guarded.
//
// It also records which product the address points at. The app rewrites the
// address as it opens that product and keeps some of these params
// (?id=55 becomes ?page=digital_products&open_product=true&product_id=55&id=55),
// so the code can later be handed back only while the address still points at
// the same product.
export const rememberPromoCodeFromUrl = () => {
    if (typeof window === 'undefined') return;
    try {
        const params = new URLSearchParams(window.location.search);
        const code = cleanCode(params.get('promo_code'));
        if (!code) return;
        window.sessionStorage.setItem(
            PROMO_CODE_STORAGE_KEY,
            JSON.stringify({ code, scope: currentScope(), at: Date.now(), target: productTarget(params) }),
        );
    } catch (e) {
        // Nothing to remember it in; a code that is still in the address works as before.
    }
};

// True when the address still points at the product the code was remembered
// for: at least one recorded param has the same value now. A link to the hub
// itself (a plain /creator?promo_code=X, which the Promo codes page copies) and
// an entry saved before targets existed have nothing recorded, and apply to
// any product on that hub.
const pointsAtRememberedProduct = (target, params) => {
    const recorded = (target && typeof target === 'object' && !Array.isArray(target))
        ? PRODUCT_PARAMS.filter((key) => typeof target[key] === 'string' && target[key] !== '')
        : [];
    if (recorded.length === 0) return true;
    return recorded.some((key) => params.get(key) === target[key]);
};

const getRememberedPromoCode = (params) => {
    try {
        const raw = window.sessionStorage.getItem(PROMO_CODE_STORAGE_KEY);
        if (!raw) return '';
        const saved = JSON.parse(raw);
        if (!saved || typeof saved !== 'object') return '';
        const age = Date.now() - saved.at;
        if (!(age >= 0 && age < PROMO_CODE_MAX_AGE_MS)) return '';
        if (saved.scope !== currentScope()) return '';
        if (!pointsAtRememberedProduct(saved.target, params)) return '';
        return cleanCode(saved.code);
    } catch (e) {
        return '';
    }
};

export const getPromoCodeFromUrl = () => {
    if (typeof window === 'undefined') return '';
    const params = new URLSearchParams(window.location.search);
    return cleanCode(params.get('promo_code')) || getRememberedPromoCode(params);
};
